import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { FinalCTA } from "@/components/site/CTAs";
import { Link } from "react-router-dom";

const TCard = ({ dot, cat, title, body, stake }) => (
  <div className="card">
    <div className="cat">
      <span className="d" style={{ background: dot }} />
      {cat}
    </div>
    <h3>{title}</h3>
    <p>{body}</p>
    <div className="stake">{stake}</div>
  </div>
);

const ForTrades = () => (
  <>
    <Seo
      title="Clearview for trades — can we afford it? Just ask."
      description="Built around how trades really run — retentions, progress claims, plant. Ask if you can afford the next move and get a straight call."
      path="/for/trades"
    />
    <Header />

    <section className="hero">
      <div className="glow" />
      <div className="container">
        <Reveal>
          <div className="eyebrow eyebrow-light">For trades &amp; construction</div>
          <h1 style={{ marginTop: 16 }}>
            Another truck?<br />Another bloke?<br />
            <span className="gtext">Ask before you commit.</span>
          </h1>
          <p className="sub">
            You run on jobs, progress claims and plant — not spreadsheets. Clearview reads your real numbers and tells you straight whether you can afford the next move, any time you need it.
          </p>
          <div className="cta">
            <Link className="pill" to="/apply?src=trades" data-testid="trades-hero-cta">Book a free demo</Link>
            <a className="ghost" href="#decisions" style={{ color: "#fff" }}>Or see what it answers <span className="arr">↓</span></a>
          </div>
          <div className="chips">
            <span>Reads Xero / QB / WorkflowMax</span><span>Read-only</span><span>No lock-in</span><span>Aussie-built</span>
          </div>
        </Reveal>

        <Reveal>
          <div className="pcard">
            <div className="top">
              <span><span className="dot" />Ask Clearview</span>
              <span className="refused">Refused 35 in 24h</span>
            </div>
            <div className="body">
              <div className="ask"><span>Can we afford a second tipper and a driver — about $140k?</span></div>
              <div className="lab">Answer</div>
              <div className="ans">
                <span className="no">Not this quarter.</span> You've $190k cash, but $96k is retentions you can't draw yet and Westbrook's $42k claim is 38 days late. Real available cash is ~$70k — a $140k commitment puts you underwater if one claim slips.
              </div>
              <div className="best">
                <div className="l">⚡ Best move now</div>
                <p>Chase Westbrook's $42k progress claim and the $18k in unbilled variations — that's $60k owed before you borrow a cent.</p>
              </div>
              <div className="opts">
                <div className="opt"><div className="tt">Safest</div><div className="h">Wait one claim cycle</div></div>
                <div className="opt"><div className="tt">Growth</div><div className="h">Finance the tipper</div></div>
                <div className="opt"><div className="tt">Bold</div><div className="h">Hire driver first</div></div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="band-grey pad" id="decisions">
      <div className="container">
        <Reveal className="center">
          <div className="eyebrow">The calls that actually cost you</div>
          <h2 style={{ fontSize: "clamp(28px,4.4vw,44px)", color: "var(--navy)", marginTop: 14 }}>
            Built around how trades really run.
          </h2>
          <p className="sub-h">
            Retentions, progress claims, plant, the one big builder who pays slow. Ask it the ones that keep you up.
          </p>
        </Reveal>
        <Reveal className="grid g3" style={{ marginTop: 44 }}>
          <TCard dot="var(--t)" cat="Plant" title={'"Can we afford the excavator / truck?"'}
            body="It runs the buy against retentions you can't touch and claims still out — then hands you three costed options."
            stake="A $140k call you can't unwind" />
          <TCard dot="var(--b)" cat="Cashflow" title={'"Will we cover wages before the next claim?"'}
            body="It maps the gap between progress payments and payroll, and flags the week it gets tight."
            stake="The fortnight that sinks good builders" />
          <TCard dot="var(--p)" cat="Debtors" title={'"Which builder is about to leave me short?"'}
            body="It spots the slow payer and the over-concentration before it becomes a write-off."
            stake="One bad builder can take the year" />
          <TCard dot="var(--m)" cat="Job costing" title={'"Are we actually making money on this job?"'}
            body="It finds the jobs and clients quietly running at a loss once you count the variations and the rework."
            stake="The job you'd be better off not winning" />
          <TCard dot="var(--t)" cat="Hiring" title={'"Can we put on a leading hand?"'}
            body="It checks the pipeline and the cover before you commit to another full-time wage."
            stake="$90k+/yr you can't easily reverse" />
          <TCard dot="var(--p)" cat="Variations" title={'"What haven\'t we invoiced yet?"'}
            body="It surfaces the unbilled variations and retentions sitting as cash you've already earned."
            stake="Money you did the work for, unclaimed" />
        </Reveal>
      </div>
    </section>

    <section className="pad center">
      <div className="container" style={{ maxWidth: 760 }}>
        <Reveal>
          <div className="eyebrow">Why trades trust it</div>
          <h2 style={{ fontSize: "clamp(26px,4vw,38px)", color: "var(--navy)", marginTop: 14 }}>
            It talks like your best <span className="gtext">foreman</span>, not your accountant.
          </h2>
          <p className="sub-h">
            No jargon, no "consult a professional," no dashboard to learn. You ask in plain English, it gives you a straight call and the next move — read-only, so it can't touch your money.
          </p>
        </Reveal>
      </div>
    </section>

    <FinalCTA
      title="Can you afford the next move?"
      highlight="Ask it."
      body="Connect read-only and get a straight answer on your real numbers — in the next few minutes."
      testIdPrefix="trades-final"
      to="/apply?src=trades"
    />

    <Footer />
  </>
);

export default ForTrades;
